import { AlertCircle, CreditCard, Clock } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { BILLING_ENABLED } from '../config/features'

interface GracePeriodInfo {
  isInGracePeriod: boolean
  stage: number
  gracePeriodEnd: string | null
}

export default function GracePeriodBanner() {
  const [gracePeriodInfo, setGracePeriodInfo] = useState<GracePeriodInfo | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchGracePeriodStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) return

        // Check if user is in grace period
        const { data: isInGracePeriodData, error: isInError } = await supabase
          .rpc('is_in_grace_period', { user_id: user.id })

        if (isInError) throw isInError

        if (!isInGracePeriodData) {
          setGracePeriodInfo(null)
          setLoading(false)
          return
        }

        // Get grace period stage
        const { data: stageData, error: stageError } = await supabase
          .rpc('get_grace_period_stage', { user_id: user.id })

        if (stageError) throw stageError

        // Get grace period end date
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('grace_period_end')
          .eq('id', user.id)
          .single()

        if (profileError) throw profileError

        setGracePeriodInfo({
          isInGracePeriod: isInGracePeriodData,
          stage: stageData || 0,
          gracePeriodEnd: profileData?.grace_period_end || null
        })
      } catch (error) {
        console.error('Error fetching grace period status:', error)
        setGracePeriodInfo(null)
      } finally {
        setLoading(false)
      }
    }

    fetchGracePeriodStatus()
  }, [])

  if (loading || !gracePeriodInfo?.isInGracePeriod) {
    return null
  }

  const { stage, gracePeriodEnd } = gracePeriodInfo

  // Calculate remaining days
  const getRemainingDays = () => {
    if (!gracePeriodEnd) return 0
    const end = new Date(gracePeriodEnd)
    const now = new Date()
    const diffTime = end.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return Math.max(0, diffDays)
  }

  const remainingDays = getRemainingDays()

  // Get message and style based on stage
  const getBannerContent = () => {
    switch (stage) {
      case 1:
        return {
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          textColor: 'text-yellow-800',
          icon: <Clock className="h-5 w-5 text-yellow-600" />,
          title: '支払い確認中（制限付きモード）',
          message: `新規作成: プロジェクト2個まで、タスク10個/プロジェクトまで。プロジェクトが3個以上ある場合、タスクは追加できません。残り${remainingDays}日`,
          buttonText: '今すぐ支払う',
          buttonColor: 'bg-yellow-600 hover:bg-yellow-700'
        }
      case 2:
        return {
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          textColor: 'text-orange-800',
          icon: <AlertCircle className="h-5 w-5 text-orange-600" />,
          title: '読み取り専用モード',
          message: `データの閲覧のみ可能です。編集・新規作成はできません。残り${remainingDays}日後にファイルが自動削除されます。`,
          buttonText: '今すぐ支払う',
          buttonColor: 'bg-orange-600 hover:bg-orange-700'
        }
      case 3:
        return {
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          textColor: 'text-red-800',
          icon: <AlertCircle className="h-5 w-5 text-red-600" />,
          title: 'ファイル自動削除中',
          message: 'ファイルが自動削除されています。プロジェクトとタスクは保持されています。',
          buttonText: '今すぐ支払って復旧',
          buttonColor: 'bg-red-600 hover:bg-red-700'
        }
      default:
        return null
    }
  }

  const content = getBannerContent()
  if (!content) return null

  return (
    <div className={`${content.bgColor} ${content.borderColor} border-l-4 p-4 mb-4 rounded-r-lg shadow-sm`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          {content.icon}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`text-sm font-semibold ${content.textColor} mb-1`}>
            {content.title}
          </h3>
          <p className={`text-sm ${content.textColor}`}>
            {content.message}
          </p>
        </div>
        {/* 無料モードでは請求管理ページが無効のためボタンを出さない */}
        {BILLING_ENABLED && (
          <div className="flex-shrink-0">
            <Link
              to="/billing"
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white ${content.buttonColor} rounded-lg transition-colors`}
            >
              <CreditCard className="h-4 w-4" />
              {content.buttonText}
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}
